// ObjectiveDiscovery.js - Découverte et sélection d'objectifs
// Module fonctionnel : Recherche, filtrage et sélection d'objectifs existants

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { 
  Target, Search, Plus, ArrowRight, Lightbulb, Clock,
  Zap, Star, Loader2, Menu
} from 'lucide-react';

// ============================================================================
// CONFIGURATION DES TYPES D'OBJECTIFS
// ============================================================================

const objectiveTypes = [
  { id: 'project', label: 'Projet' },
  { id: 'idea', label: 'Idée' },
  { id: 'skill', label: 'Compétence' },
  { id: 'career', label: 'Carrière' },
  { id: 'learning', label: 'Apprentissage' },
  { id: 'desire', label: 'Désir' }
];

// ============================================================================
// FILTRES MOBILES AVEC DROPDOWN
// ============================================================================

const MobileFilters = ({ 
  filterType, 
  filterTheme, 
  uniqueThemes, 
  searchQuery,
  onFilterTypeChange,
  onFilterThemeChange,
  onReset
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const hasActiveFilters = filterType !== 'all' || filterTheme !== 'all';

  return (
    <div className="space-y-2">
      <button
        onClick={() => setShowFilters(!showFilters)}
        className="flex items-center justify-between w-full px-4 py-2 bg-gray-50 rounded-lg text-sm text-gray-700"
      >
        <span>Filtres {hasActiveFilters && '(actifs)'}</span>
        <Menu className={`h-4 w-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
      </button>
      
      {showFilters && (
        <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-3">
          <select 
            value={filterType}
            onChange={(e) => onFilterTypeChange(e.target.value)}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 bg-white text-gray-600 text-sm focus:ring-2 focus:ring-purple-500"
          >
            <option value="all">Tous les types</option>
            {objectiveTypes.map(type => (
              <option key={type.id} value={type.id}>{type.label}</option>
            ))}
          </select>

          {uniqueThemes.length > 0 && (
            <select 
              value={filterTheme}
              onChange={(e) => onFilterThemeChange(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 bg-white text-gray-600 text-sm focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">Tous les domaines</option>
              {uniqueThemes.map(theme => (
                <option key={theme} value={theme}>{theme}</option>
              ))}
            </select>
          )}

          {(searchQuery || filterType !== 'all' || filterTheme !== 'all') && (
            <button
              onClick={onReset}
              className="w-full px-3 py-2 text-sm text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Réinitialiser
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// STATISTIQUES RESPONSIVE
// ============================================================================

const StatisticsOverview = ({ filteredObjectives, uniqueThemes, statsData, isMobile }) => {
  return (
    <div className={`bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl ${isMobile ? 'p-3' : 'p-4'} border border-blue-200`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold text-gray-800`}>Vue d'ensemble</h3>
        <div className={`flex items-center gap-2 ${isMobile ? 'text-xs' : 'text-sm'} text-gray-600`}>
          <Zap className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`} />
          <span>{statsData.completedActions}/{statsData.totalActions} actions</span>
        </div>
      </div>
      
      <div className={`grid ${isMobile ? 'grid-cols-2 gap-3' : 'grid-cols-2 md:grid-cols-4 gap-4'} text-center`}>
        <div>
          <div className={`${isMobile ? 'text-lg' : 'text-2xl'} font-bold text-blue-600`}>{filteredObjectives.length}</div>
          <div className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-600`}>Objectifs</div>
        </div>
        <div>
          <div className={`${isMobile ? 'text-lg' : 'text-2xl'} font-bold text-green-600`}>{uniqueThemes.length}</div>
          <div className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-600`}>Domaines</div>
        </div>
        {!isMobile && (
          <>
            <div>
              <div className="text-2xl font-bold text-purple-600">{statsData.totalActions}</div>
              <div className="text-sm text-gray-600">Actions totales</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-orange-600">{statsData.completedActions}</div>
              <div className="text-sm text-gray-600">Actions achevées</div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// FORMULAIRE DE RECHERCHE D'OBJECTIFS
// ============================================================================

export const ObjectiveSearchForm = React.memo(({ 
  userObjectives, 
  onSelectObjective, 
  onBack, 
  isMobile, 
  isTablet,
  processObjectiveData,
  getDomainInfo,
  calculateStats,
  filterObjectives
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterTheme, setFilterTheme] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  
  const searchInputRef = useRef(null);

  // Focus automatique
  useEffect(() => {
    const timer = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Traitement des données
  const processedObjectives = useMemo(() => {
    return userObjectives.map(processObjectiveData);
  }, [userObjectives, processObjectiveData]);

  // Filtrage
  const filteredObjectives = useMemo(() => {
    return filterObjectives(processedObjectives, { searchQuery, filterType, filterTheme });
  }, [processedObjectives, searchQuery, filterType, filterTheme, filterObjectives]);

  // Domaines uniques
  const uniqueThemes = useMemo(() => {
    const domains = new Set();
    filteredObjectives.forEach(obj => {
      const domain = obj.chapitre || obj.theme || 'Non classé';
      domains.add(domain);
    });
    return [...domains].sort();
  }, [filteredObjectives]);

  // Statistiques
  const statsData = useMemo(() => {
    return calculateStats(filteredObjectives);
  }, [filteredObjectives, calculateStats]);

  // Handlers
  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  const handleReset = useCallback(() => {
    setSearchQuery('');
    setFilterType('all');
    setFilterTheme('all');
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (processedObjectives.length === 0) {
    return (
      <div className="text-center py-8">
        <Target className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h4 className={`${isMobile ? 'text-base' : 'text-lg'} font-medium text-gray-600 mb-2`}>
          Aucun objectif trouvé
        </h4>
        <p className={`text-gray-500 ${isMobile ? 'text-sm' : ''} mb-6`}>
          Commencez par créer votre premier objectif
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistiques globales */}
      <StatisticsOverview 
        filteredObjectives={filteredObjectives}
        uniqueThemes={uniqueThemes}
        statsData={statsData}
        isMobile={isMobile}
      />

      {/* Barre de recherche et filtres */}
      <div className="space-y-4">
        {/* Recherche */}
        <div className="relative">
          <Search className={`absolute ${isMobile ? 'left-3' : 'left-3'} top-1/2 transform -translate-y-1/2 text-gray-400 ${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Rechercher un objectif..."
            value={searchQuery}
            onChange={handleSearchChange}
            className={`w-full ${isMobile ? 'pl-9 pr-4 py-2 text-sm' : 'pl-10 pr-4 py-3'} rounded-lg border border-gray-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
          />
        </div>

        {/* Filtres */}
        {isMobile ? (
          <MobileFilters
            filterType={filterType}
            filterTheme={filterTheme}
            uniqueThemes={uniqueThemes}
            searchQuery={searchQuery}
            onFilterTypeChange={setFilterType}
            onFilterThemeChange={setFilterTheme}
            onReset={handleReset}
          />
        ) : (
          <div className="flex flex-wrap gap-3">
            <select 
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="rounded-lg border border-gray-200 px-4 py-2 bg-white text-gray-600 focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">Tous les types</option>
              {objectiveTypes.map(type => (
                <option key={type.id} value={type.id}>{type.label}</option>
              ))}
            </select>

            {uniqueThemes.length > 0 && (
              <select 
                value={filterTheme}
                onChange={(e) => setFilterTheme(e.target.value)}
                className="rounded-lg border border-gray-200 px-4 py-2 bg-white text-gray-600 focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">Tous les domaines</option>
                {uniqueThemes.map(theme => (
                  <option key={theme} value={theme}>{theme}</option>
                ))}
              </select>
            )}

            {(searchQuery || filterType !== 'all' || filterTheme !== 'all') && (
              <button
                onClick={handleReset}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Réinitialiser
              </button>
            )}
          </div>
        )}
      </div>

      {/* Statistiques par domaine - Desktop uniquement */}
      {!isMobile && filterTheme === 'all' && Object.keys(statsData.domainStats).length > 1 && Object.keys(statsData.domainStats).length <= 6 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
            <Target className="w-4 h-4 text-blue-500" />
            Répartition par domaine
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(statsData.domainStats).map(([domain, stats]) => {
              const domainInfo = getDomainInfo(domain);
              return (
                <button
                  key={domain}
                  onClick={() => setFilterTheme(domain)}
                  className={`p-3 ${domainInfo.bg} hover:bg-blue-50 rounded-lg ${domainInfo.border} hover:border-blue-300 transition-colors text-left border`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    {domainInfo.iconSmall}
                    <div className={`font-medium text-sm ${domainInfo.text} truncate`}>{domain}</div>
                  </div>
                  <div className="text-xs text-gray-500 space-y-1">
                    <div>{stats.count} objectif{stats.count > 1 ? 's' : ''}</div>
                    <div className="flex items-center gap-1">
                      <Zap className="w-3 h-3" />
                      {stats.completedActions}/{stats.actions} actions
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Résultats */}
      {filteredObjectives.length === 0 ? (
        <div className="text-center py-8">
          <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h4 className={`${isMobile ? 'text-base' : 'text-lg'} font-medium text-gray-600 mb-2`}>Aucun résultat</h4>
          <p className={`text-gray-500 ${isMobile ? 'text-sm' : ''} mb-4`}>
            Aucun objectif ne correspond à vos critères de recherche
          </p>
          <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-400`}>
            {processedObjectives.length} objectif{processedObjectives.length > 1 ? 's' : ''} au total
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className={`flex items-center justify-between ${isMobile ? 'text-xs' : 'text-sm'} text-gray-500 mb-4`}>
            <span>
              {filteredObjectives.length} objectif{filteredObjectives.length > 1 ? 's' : ''} trouvé{filteredObjectives.length > 1 ? 's' : ''}
              {filteredObjectives.length !== processedObjectives.length && ` sur ${processedObjectives.length}`}
            </span>
          </div>
          
          <div className={`${isMobile ? 'max-h-80' : 'max-h-96'} overflow-y-auto space-y-3`}>
            {filteredObjectives.map((objective) => {
              const domainInfo = getDomainInfo(objective.chapitre || objective.theme);
              
              return (
                <div 
                  key={objective.id}
                  onClick={() => onSelectObjective(objective)}
                  className={`flex items-center ${isMobile ? 'p-3' : 'p-4'} bg-gray-50 rounded-lg hover:bg-blue-50 cursor-pointer transition-colors border border-gray-200 hover:border-blue-300`}
                >
                  <div className={`${isMobile ? 'w-10 h-10' : 'w-12 h-12'} bg-blue-100 rounded-lg flex items-center justify-center ${isMobile ? 'mr-3' : 'mr-4'}`}>
                    {isMobile ? domainInfo.iconSmall : domainInfo.iconSmall}
                  </div>
                  <div className="flex-grow min-w-0">
                    <h5 className={`font-medium text-gray-800 mb-1 ${isMobile ? 'text-sm' : ''} truncate`}>
                      {objective.title}
                    </h5>
                    <div className={`flex items-center gap-2 ${isMobile ? 'text-xs' : 'text-sm'} text-gray-500 mb-1 flex-wrap`}>
                      {objective.type && (
                        <span className={`px-2 py-1 bg-gray-200 rounded ${isMobile ? 'text-xs' : 'text-xs'}`}>
                          {objectiveTypes.find(type => type.id === objective.type)?.label || objective.type}
                        </span>
                      )}
                      {(objective.chapitre || objective.theme) && (
                        <span className={`px-2 py-1 bg-blue-100 text-blue-700 rounded ${isMobile ? 'text-xs' : 'text-xs'} truncate max-w-24`}>
                          {objective.chapitre || objective.theme}
                        </span>
                      )}
                      <span className={`px-2 py-1 bg-green-100 text-green-700 rounded ${isMobile ? 'text-xs' : 'text-xs'} flex items-center gap-1`}>
                        <Zap className="w-3 h-3" />
                        {objective.completedActionsCount}/{objective.actionsCount}
                      </span>
                      {objective.keyConceptsCount > 0 && !isMobile && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs flex items-center gap-1">
                          <Star className="w-3 h-3" />
                          {objective.keyConceptsCount} concepts
                        </span>
                      )}
                    </div>
                    <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-500`}>
                      {objective.updatedAt ? 
                        `Modifié le ${objective.updatedAt.toDate().toLocaleDateString()}` :
                        'Récent'
                      }
                    </p>
                    {objective.description && !isMobile && (
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">{objective.description}</p>
                    )}
                  </div>
                  <ArrowRight className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} text-gray-400 flex-shrink-0`} />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});

ObjectiveSearchForm.displayName = 'ObjectiveSearchForm';

// ============================================================================
// SÉLECTION PRINCIPALE D'OBJECTIFS
// ============================================================================

export const ObjectiveSelection = React.memo(({ 
  userObjectives, 
  onSelectObjective, 
  onCreateObjective, 
  isMobile, 
  isTablet,
  processObjectiveData,
  getDomainInfo
}) => {
  // Traiter les objectifs pour l'affichage des récents
  const processedObjectives = useMemo(() => {
    return userObjectives.map(processObjectiveData);
  }, [userObjectives, processObjectiveData]);

  return (
    <div className={`bg-white rounded-xl shadow-lg border border-gray-200 ${isMobile ? 'p-4' : 'p-8'}`}>
      <div className="text-center mb-8">
        <h3 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold text-gray-800 mb-4`}>
          Définir votre ambition
        </h3>
        <p className={`text-gray-600 ${isMobile ? 'text-sm' : ''} mb-6`}>
          Commencez par choisir votre approche
        </p>
      </div>

      <div className={`grid ${isMobile ? 'grid-cols-1 gap-4' : 'md:grid-cols-2 gap-6'} mb-8`}>
        {/* Nouvel objectif */}
        <div className={`bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl ${isMobile ? 'p-4' : 'p-6'} border border-purple-200 hover:shadow-lg transition-shadow`}>
          <div className="flex items-center mb-4">
            <div className={`${isMobile ? 'w-10 h-10' : 'w-12 h-12'} bg-purple-500 rounded-lg flex items-center justify-center ${isMobile ? 'mr-3' : 'mr-4'}`}>
              <Plus className={`${isMobile ? 'w-5 h-5' : 'w-6 h-6'} text-white`} />
            </div>
            <h4 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold text-gray-800`}>Nouvel objectif</h4>
          </div>
          <p className={`text-gray-600 ${isMobile ? 'text-sm' : ''} mb-4`}>
            Commencez un nouveau parcours stratégique
          </p>
          <button
            onClick={onCreateObjective}
            className={`w-full ${isMobile ? 'py-2' : 'py-3'} bg-gradient-to-r from-purple-500 to-blue-600 text-white rounded-lg hover:from-purple-600 hover:to-blue-700 transition-colors flex items-center justify-center gap-2 ${isMobile ? 'text-sm' : ''}`}
          >
            <Lightbulb className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'}`} />
            Créer un nouvel objectif
          </button>
        </div>

        {/* Objectif existant */}
        <div className={`bg-gradient-to-br from-green-50 to-teal-50 rounded-xl ${isMobile ? 'p-4' : 'p-6'} border border-green-200 hover:shadow-lg transition-shadow`}>
          <div className="flex items-center mb-4">
            <div className={`${isMobile ? 'w-10 h-10' : 'w-12 h-12'} bg-green-500 rounded-lg flex items-center justify-center ${isMobile ? 'mr-3' : 'mr-4'}`}>
              <Target className={`${isMobile ? 'w-5 h-5' : 'w-6 h-6'} text-white`} />
            </div>
            <h4 className={`${isMobile ? 'text-lg' : 'text-xl'} font-semibold text-gray-800`}>Choisir un objectif</h4>
          </div>
          <p className={`text-gray-600 ${isMobile ? 'text-sm' : ''} mb-4`}>
            Reprenez le travail sur un de vos objectifs existants
          </p>
          <button
            onClick={() => onSelectObjective('list')}
            className={`w-full ${isMobile ? 'py-2' : 'py-3'} bg-gradient-to-r from-green-500 to-teal-600 text-white rounded-lg hover:from-green-600 hover:to-teal-700 transition-colors flex items-center justify-center gap-2 ${isMobile ? 'text-sm' : ''}`}
          >
            <Target className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'}`} />
            Choisir un objectif
          </button>
        </div>
      </div>

      {/* Objectifs récents */}
      {processedObjectives.length > 0 && (
        <div className={`bg-gray-50 rounded-xl ${isMobile ? 'p-4' : 'p-6'}`}>
          <h4 className={`${isMobile ? 'text-base' : 'text-lg'} font-semibold text-gray-800 mb-4 flex items-center`}>
            <Clock className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} mr-2 text-blue-500`} />
            Vos objectifs récents
          </h4>
          <div className="space-y-3">
            {processedObjectives.slice(0, isMobile ? 2 : 3).map((objective) => {
              const domainInfo = getDomainInfo(objective.chapitre || objective.theme);
              return (
                <div 
                  key={objective.id}
                  onClick={() => onSelectObjective(objective)}
                  className={`flex items-center ${isMobile ? 'p-2' : 'p-3'} bg-white rounded-lg hover:bg-blue-50 cursor-pointer transition-colors border border-gray-200 hover:border-blue-300`}
                >
                  <div className={`${isMobile ? 'w-8 h-8' : 'w-10 h-10'} bg-blue-100 rounded-lg flex items-center justify-center ${isMobile ? 'mr-2' : 'mr-3'}`}>
                    {domainInfo.iconSmall}
                  </div>
                  <div className="flex-grow min-w-0">
                    <h5 className={`font-medium text-gray-800 ${isMobile ? 'text-sm' : ''} truncate`}>{objective.title}</h5>
                    <div className={`flex items-center gap-2 ${isMobile ? 'text-xs' : 'text-xs'} text-gray-500 mt-1 flex-wrap`}>
                      {(objective.chapitre || objective.theme) && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded truncate max-w-20">
                          {objective.chapitre || objective.theme}
                        </span>
                      )}
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded flex items-center gap-1">
                        <Zap className="w-3 h-3" />
                        {objective.completedActionsCount}/{objective.actionsCount}
                      </span>
                      {objective.keyConceptsCount > 0 && !isMobile && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded flex items-center gap-1">
                          <Star className="w-3 h-3" />
                          {objective.keyConceptsCount}
                        </span>
                      )}
                    </div>
                    <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-500 mt-1`}>
                      {objective.updatedAt ? 
                        `Modifié le ${objective.updatedAt.toDate().toLocaleDateString()}` :
                        'Récent'
                      }
                    </p>
                  </div>
                  <ArrowRight className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} text-gray-400 flex-shrink-0`} />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});

ObjectiveSelection.displayName = 'ObjectiveSelection';